import React, { useEffect, useMemo, useState, useCallback } from 'react';
import { useParams } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import { useCampaign } from '../context/CampaignContext';
import { t } from '../lib/i18n';
import { MainLayout } from '../components/layout/MainLayout';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from '../components/ui/alert-dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Badge } from '../components/ui/badge';
import { Slider } from '../components/ui/slider';
import { Switch } from '../components/ui/switch';
import { isGameMaster, formatDate, cn } from '../lib/utils';
import { toast } from 'sonner';
import {
  Plus, Gem, Coins, Sparkles, Loader2, Trash2, Edit, Check, Wand2,
  Package, ScrollText, Crown, X, Star, Copy, Search, Grid3X3, List,
  Sword, Shield, FlaskConical, Scroll, CircleDot, Zap, Weight,
  SlidersHorizontal, Heart, ArrowUpDown, Eye, RefreshCw, Save, User
} from 'lucide-react';
import { Checkbox } from '../components/ui/checkbox';
import Equipment3DCard from '../components/treasure/Equipment3DCard';

const API_URL = '/api';

// ─── Constants ───────────────────────────────────────────────────────────────

const RARITY_COLORS = {
  common: 'bg-gray-500',
  uncommon: 'bg-green-500',
  rare: 'bg-blue-500',
  very_rare: 'bg-purple-500',
  legendary: 'bg-amber-500',
  artifact: 'bg-red-600'
};

const RARITY_TEXT_COLORS = {
  common: 'text-gray-500',
  uncommon: 'text-green-500',
  rare: 'text-blue-500',
  very_rare: 'text-purple-500',
  legendary: 'text-amber-500',
  artifact: 'text-red-600'
};

const RARITY_BORDER_COLORS = {
  common: 'border-gray-400/30',
  uncommon: 'border-green-500/40',
  rare: 'border-blue-500/40',
  very_rare: 'border-purple-500/40',
  legendary: 'border-amber-500/50',
  artifact: 'border-red-600/50'
};

const TYPE_ICONS = {
  weapon: Sword, armor: Shield, potion: FlaskConical, scroll: Scroll,
  wondrous: Sparkles, ring: CircleDot, wand: Zap, staff: Zap,
  ammunition: CircleDot, tool: SlidersHorizontal, material: Package, quest_item: Crown
};

const EQUIPMENT_TYPES = ['weapon', 'armor', 'potion', 'scroll', 'wondrous', 'ring', 'wand', 'staff', 'ammunition', 'tool', 'material', 'quest_item'];
const EQUIPMENT_CATEGORIES = ['equipment', 'consumable', 'material', 'quest_item', 'artifact'];
const EQUIPMENT_RARITIES = ['common', 'uncommon', 'rare', 'very_rare', 'legendary', 'artifact'];
const EQUIPMENT_SLOTS = ['none', 'head', 'chest', 'hands', 'feet', 'neck', 'ring', 'waist', 'back', 'main_hand', 'off_hand', 'two_hands'];

const LOCATION_LABELS = {
  fr: { dungeon: 'Donjon', temple: 'Temple', noble: 'Noble', tomb: 'Tombe', dragon: 'Antre de dragon' },
  en: { dungeon: 'Dungeon', temple: 'Temple', noble: 'Noble estate', tomb: 'Tomb', dragon: 'Dragon lair' }
};

const LEVEL_TREASURE_TARGET_GP = {
  1: 80, 2: 120, 3: 180, 4: 280, 5: 450,
  6: 650, 7: 900, 8: 1200, 9: 1600, 10: 2200,
  11: 3000, 12: 4200, 13: 5800, 14: 7800, 15: 10500,
  16: 14000, 17: 18500, 18: 24000, 19: 30500, 20: 38000
};

const RARITY_BUDGET_MULTIPLIER = {
  common: 0.85, uncommon: 1.0, rare: 1.35, very_rare: 1.85, legendary: 2.4
};

const LOCATION_TREASURE_PROFILE = {
  dungeon: { budget_mult: 1.0, coins_share: 0.55, gems_share: 0.28, art_share: 0.17, magic_bonus: 0.0 },
  temple: { budget_mult: 1.1, coins_share: 0.42, gems_share: 0.25, art_share: 0.33, magic_bonus: 0.05 },
  noble: { budget_mult: 1.2, coins_share: 0.35, gems_share: 0.22, art_share: 0.43, magic_bonus: 0.08 },
  tomb: { budget_mult: 1.25, coins_share: 0.4, gems_share: 0.35, art_share: 0.25, magic_bonus: 0.1 },
  dragon: { budget_mult: 2.0, coins_share: 0.5, gems_share: 0.35, art_share: 0.15, magic_bonus: 0.2 }
};

// ─── Helpers ─────────────────────────────────────────────────────────────────

const clampInt = (value, min, max) => Math.max(min, Math.min(max, parseInt(value, 10) || min));

const levelFromBudget = (budgetGp) => {
  const entries = Object.entries(LEVEL_TREASURE_TARGET_GP).map(([level, gp]) => ({ level: parseInt(level, 10), gp }));
  return entries.reduce((best, current) => Math.abs(current.gp - budgetGp) < Math.abs(best.gp - budgetGp) ? current : best).level;
};

const computeGenerationPreview = (params) => {
  const method = ['level', 'cr', 'budget', 'location', 'rarity'].includes(params.method) ? params.method : 'level';
  const locationType = LOCATION_TREASURE_PROFILE[params.location_type] ? params.location_type : 'dungeon';
  const rarity = RARITY_BUDGET_MULTIPLIER[params.rarity] ? params.rarity : 'uncommon';
  const profile = LOCATION_TREASURE_PROFILE[locationType];
  const rarityMult = RARITY_BUDGET_MULTIPLIER[rarity];
  const cr = clampInt(params.cr, 1, 30);
  const budgetGp = Math.max(25, parseInt(params.budget_gp, 10) || 25);

  let level = clampInt(params.level, 1, 20);
  if (method === 'cr') level = clampInt(Math.round((cr + 1) / 1.5), 1, 20);
  else if (method === 'budget') level = levelFromBudget(budgetGp);

  let baseGp = LEVEL_TREASURE_TARGET_GP[level];
  if (method === 'budget') baseGp = Math.max(25, budgetGp);
  else if (method === 'cr') baseGp = LEVEL_TREASURE_TARGET_GP[level] * (1 + (cr / 40));
  else if (method === 'rarity') baseGp = LEVEL_TREASURE_TARGET_GP[level] * rarityMult;

  if (['level', 'cr', 'location'].includes(method)) baseGp = baseGp * (1 + ((rarityMult - 1) * 0.2));
  baseGp = baseGp * profile.budget_mult;
  if (method === 'location') baseGp = baseGp * 1.1;

  const minFactor = method === 'budget' ? 0.92 : 0.85;
  const maxFactor = method === 'budget' ? 1.08 : 1.2;
  const minGp = Math.max(25, Math.round(baseGp * minFactor));
  const maxGp = Math.max(minGp, Math.round(baseGp * maxFactor));
  const targetGp = Math.round((minGp + maxGp) / 2);

  const coinsGp = Math.round(targetGp * profile.coins_share);
  const gemsGp = Math.round(targetGp * profile.gems_share);
  const artGp = Math.max(0, targetGp - coinsGp - gemsGp);

  let magicChance = Math.min(0.9, 0.18 + (level * 0.025) + profile.magic_bonus);
  if (method === 'budget') magicChance += Math.min(0.2, targetGp / 40000);
  else if (method === 'rarity') magicChance += 0.25;
  else if (method === 'location') magicChance += 0.08;
  magicChance = Math.round(Math.min(0.95, magicChance) * 100);

  return { level, minGp, maxGp, targetGp, coinsGp, gemsGp, artGp, magicChance };
};

const calculateTotalGP = (treasure) => {
  let total = 0;
  if (treasure.coins) {
    total += (treasure.coins.cp || 0) / 100 + (treasure.coins.sp || 0) / 10 +
             (treasure.coins.ep || 0) / 2 + (treasure.coins.gp || 0) + (treasure.coins.pp || 0) * 10;
  }
  if (treasure.gems) treasure.gems.forEach(g => total += (g.value || 0) * (g.quantity || 1));
  if (treasure.art_objects) treasure.art_objects.forEach(a => total += a.value || 0);
  return Math.round(total);
};

const emptyEquipment = () => ({
  name: '', type: 'wondrous', category: 'equipment', rarity: 'common',
  description: '', effect: '', stats: '', weight: 0, value_gp: 0,
  slot: 'none', charges: null, max_charges: null, bonus: '',
  requirements: '', tags: [], image_url: '', assigned_to: [], is_holographic: false
});

// ─── Equipment Create/Edit Dialog ────────────────────────────────────────────

function EquipmentDialog({ open, onOpenChange, equipment, language, campaignId, onSaved, campaignMembers = [] }) {
  const isEdit = !!equipment?.id;
  const [form, setForm] = useState(emptyEquipment());
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (open) {
      setForm(equipment?.id ? {
        name: equipment.name || '',
        type: equipment.type || 'wondrous',
        category: equipment.category || 'equipment',
        rarity: equipment.rarity || 'common',
        description: equipment.description || '',
        effect: equipment.effect || '',
        stats: equipment.stats || '',
        weight: equipment.weight || 0,
        value_gp: equipment.value_gp || 0,
        slot: equipment.slot || 'none',
        charges: equipment.charges,
        max_charges: equipment.max_charges,
        bonus: equipment.bonus || '',
        requirements: equipment.requirements || '',
        tags: equipment.tags || [],
        image_url: equipment.image_url || '',
        assigned_to: equipment.assigned_to || [],
        is_holographic: equipment.is_holographic || false,
      } : emptyEquipment());
    }
  }, [open, equipment]);

  const handleSave = async () => {
    if (!form.name.trim()) {
      toast.error(language === 'fr' ? 'Le nom est requis' : 'Name is required');
      return;
    }
    setSaving(true);
    try {
      const payload = { ...form, tags: form.tags.filter(Boolean) };
      if (isEdit) {
        const res = await axios.patch(`${API_URL}/equipment/${equipment.id}`, payload);
        onSaved(res.data, 'update');
      } else {
        const res = await axios.post(`${API_URL}/campaigns/${campaignId}/equipment`, payload);
        onSaved(res.data, 'create');
      }
      toast.success(t('equipment_saved', language));
      onOpenChange(false);
    } catch {
      toast.error(t('error_generic', language));
    } finally {
      setSaving(false);
    }
  };

  const setField = (key, val) => setForm(prev => ({ ...prev, [key]: val }));

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="font-cinzel">
            {isEdit ? t('equipment_edit', language) : t('equipment_create', language)}
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-6 py-2">
          {/* Info section */}
          <div className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-2 md:col-span-2">
                <Label>{t('equipment_name', language)}</Label>
                <Input value={form.name} onChange={e => setField('name', e.target.value)} />
              </div>
              <div className="space-y-2">
                <Label>Type</Label>
                <Select value={form.type} onValueChange={v => setField('type', v)}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {EQUIPMENT_TYPES.map(tp => (
                      <SelectItem key={tp} value={tp}>{t(`equipment_type_${tp}`, language)}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>{t('filter_all_categories', language)}</Label>
                <Select value={form.category} onValueChange={v => setField('category', v)}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {EQUIPMENT_CATEGORIES.map(cat => (
                      <SelectItem key={cat} value={cat}>{t(`equipment_category_${cat}`, language)}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>{t('filter_all_rarities', language)}</Label>
                <Select value={form.rarity} onValueChange={v => setField('rarity', v)}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {EQUIPMENT_RARITIES.map(r => (
                      <SelectItem key={r} value={r}>
                        <span className="flex items-center gap-2">
                          <div className={cn("h-2 w-2 rounded-full", RARITY_COLORS[r])} />
                          {t(`rarity_${r}`, language)}
                        </span>
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>{t('equipment_slot', language)}</Label>
                <Select value={form.slot} onValueChange={v => setField('slot', v)}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {EQUIPMENT_SLOTS.map(s => (
                      <SelectItem key={s} value={s}>{t(`slot_${s}`, language)}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            {/* Holographic toggle */}
            <div className="flex items-center gap-3 pt-1">
              <Switch
                checked={form.is_holographic || false}
                onCheckedChange={v => setField('is_holographic', v)}
                id="holo-toggle"
              />
              <Label htmlFor="holo-toggle" className="cursor-pointer flex items-center gap-2">
                <Sparkles className="h-4 w-4 text-purple-500" />
                {t('equipment_holographic', language)}
              </Label>
            </div>
          </div>

          {/* Description / Effect */}
          <div className="space-y-4">
            <div className="space-y-2">
              <Label>{t('equipment_description', language)}</Label>
              <Textarea value={form.description} onChange={e => setField('description', e.target.value)} rows={3} />
            </div>
            <div className="space-y-2">
              <Label>{t('equipment_effect', language)}</Label>
              <Textarea value={form.effect} onChange={e => setField('effect', e.target.value)} rows={3} />
            </div>
            <div className="space-y-2">
              <Label>{t('equipment_stats', language)}</Label>
              <Input value={form.stats} onChange={e => setField('stats', e.target.value)} />
            </div>
          </div>

          {/* Numeric fields */}
          <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 gap-4">
            <div className="space-y-2">
              <Label>{t('equipment_value', language)}</Label>
              <Input type="number" min={0} value={form.value_gp} onChange={e => setField('value_gp', parseInt(e.target.value) || 0)} />
            </div>
            <div className="space-y-2">
              <Label>{t('equipment_weight', language)}</Label>
              <Input type="number" min={0} step={0.1} value={form.weight} onChange={e => setField('weight', parseFloat(e.target.value) || 0)} />
            </div>
            <div className="space-y-2">
              <Label>{t('equipment_charges', language)}</Label>
              <Input type="number" min={0} value={form.charges ?? ''} onChange={e => setField('charges', e.target.value === '' ? null : parseInt(e.target.value) || 0)} />
            </div>
            <div className="space-y-2">
              <Label>{t('equipment_max_charges', language)}</Label>
              <Input type="number" min={0} value={form.max_charges ?? ''} onChange={e => setField('max_charges', e.target.value === '' ? null : parseInt(e.target.value) || 0)} />
            </div>
          </div>

          {/* Bonus, Requirements, Tags, Image */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label>{t('equipment_bonus', language)}</Label>
              <Input value={form.bonus} onChange={e => setField('bonus', e.target.value)} placeholder="+1, +2..." />
            </div>
            <div className="space-y-2">
              <Label>{t('equipment_requirements', language)}</Label>
              <Input value={form.requirements} onChange={e => setField('requirements', e.target.value)} />
            </div>
            <div className="space-y-2">
              <Label>{t('equipment_tags', language)}</Label>
              <Input
                value={(form.tags || []).join(', ')}
                onChange={e => setField('tags', e.target.value.split(',').map(s => s.trim()))}
              />
            </div>
            <div className="space-y-2">
              <Label>{t('equipment_image_url', language)}</Label>
              <Input value={form.image_url} onChange={e => setField('image_url', e.target.value)} />
            </div>
          </div>

          {/* Assign to players */}
          {campaignMembers.filter(m => m.role === 'member').length > 0 && (
            <div className="space-y-3">
              <Label className="flex items-center gap-2">
                <User className="h-4 w-4" />
                {t('equipment_assign_to', language)}
              </Label>
              <div className="space-y-2 max-h-40 overflow-y-auto rounded-md border p-3">
                {campaignMembers.filter(m => m.role === 'member').map(member => {
                  const isAssigned = (form.assigned_to || []).some(a => a.user_id === member.user_id);
                  return (
                    <label key={member.user_id} className="flex items-center gap-3 cursor-pointer py-1 hover:bg-accent/50 rounded px-1">
                      <Checkbox
                        checked={isAssigned}
                        onCheckedChange={(checked) => {
                          if (checked) {
                            setField('assigned_to', [...(form.assigned_to || []), {
                              user_id: member.user_id,
                              username: member.username,
                              character_name: member.character_name || ''
                            }]);
                          } else {
                            setField('assigned_to', (form.assigned_to || []).filter(a => a.user_id !== member.user_id));
                          }
                        }}
                      />
                      <div className="flex-1 min-w-0">
                        <span className="text-sm font-medium">{member.username}</span>
                        {member.character_name && (
                          <span className="text-xs text-muted-foreground ml-2">({member.character_name})</span>
                        )}
                      </div>
                    </label>
                  );
                })}
              </div>
            </div>
          )}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>{t('cancel', language)}</Button>
          <Button onClick={handleSave} disabled={saving} className="fantasy-button">
            {saving ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Save className="mr-2 h-4 w-4" />}
            {t('save', language)}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ─── Equipment Detail Dialog ─────────────────────────────────────────────────

function EquipmentDetailDialog({ open, onOpenChange, item, language, onEdit, onDuplicate, onToggleFavorite, onDelete }) {
  if (!item) return null;
  const TypeIcon = TYPE_ICONS[item.type] || Sparkles;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle className="font-cinzel text-xl">{item.name}</DialogTitle>
          <DialogDescription className="flex items-center gap-2 pt-1">
            <Badge className={cn("text-white", RARITY_COLORS[item.rarity])}>
              {t(`rarity_${item.rarity}`, language)}
            </Badge>
            <Badge variant="outline">
              <TypeIcon className="h-3 w-3 mr-1" />
              {t(`equipment_type_${item.type}`, language)}
            </Badge>
            {item.slot !== 'none' && (
              <Badge variant="secondary">{t(`slot_${item.slot}`, language)}</Badge>
            )}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-2">
          {item.image_url && (
            <img src={item.image_url} alt={item.name} className="w-full h-48 object-cover rounded-lg" />
          )}

          {item.description && (
            <div>
              <Label className="text-xs text-muted-foreground">{t('equipment_description', language)}</Label>
              <p className="text-sm mt-1">{item.description}</p>
            </div>
          )}

          {item.effect && (
            <div className="p-3 rounded-lg bg-primary/5 border border-primary/20">
              <Label className="text-xs text-primary">{t('equipment_effect', language)}</Label>
              <p className="text-sm mt-1">{item.effect}</p>
            </div>
          )}

          <div className="grid grid-cols-2 gap-3 text-sm">
            {item.value_gp > 0 && (
              <div className="flex items-center gap-2">
                <Coins className="h-4 w-4 text-amber-500" />
                <span>{item.value_gp} gp</span>
              </div>
            )}
            {item.weight > 0 && (
              <div className="flex items-center gap-2">
                <Weight className="h-4 w-4 text-muted-foreground" />
                <span>{item.weight} lb</span>
              </div>
            )}
            {item.bonus && (
              <div className="flex items-center gap-2">
                <Zap className="h-4 w-4 text-blue-500" />
                <span>{item.bonus}</span>
              </div>
            )}
            {item.charges != null && (
              <div className="flex items-center gap-2">
                <FlaskConical className="h-4 w-4 text-purple-500" />
                <span>{item.charges}/{item.max_charges || '?'}</span>
              </div>
            )}
          </div>

          {item.requirements && (
            <p className="text-xs text-muted-foreground italic">{t('equipment_requirements', language)}: {item.requirements}</p>
          )}

          {item.tags?.length > 0 && (
            <div className="flex flex-wrap gap-1">
              {item.tags.map((tag, i) => (
                <Badge key={i} variant="outline" className="text-xs">{tag}</Badge>
              ))}
            </div>
          )}
        </div>

        <DialogFooter className="flex justify-between">
          <div className="flex gap-2">
            <Button variant="ghost" size="sm" onClick={() => { onToggleFavorite(item.id); onOpenChange(false); }}>
              <Star className={cn("h-4 w-4 mr-1", item.is_favorite ? "fill-amber-500 text-amber-500" : "")} />
              {t('equipment_toggle_favorite', language)}
            </Button>
            <Button variant="ghost" size="sm" onClick={() => { onDuplicate(item.id); onOpenChange(false); }}>
              <Copy className="h-4 w-4 mr-1" />
              {t('equipment_duplicate', language)}
            </Button>
          </div>
          <div className="flex gap-2">
            <Button variant="destructive" size="sm" onClick={() => { onDelete(item); onOpenChange(false); }}>
              <Trash2 className="h-4 w-4 mr-1" />
            </Button>
            <Button size="sm" onClick={() => { onEdit(item); onOpenChange(false); }}>
              <Edit className="h-4 w-4 mr-1" />
              {t('edit', language)}
            </Button>
          </div>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ═════════════════════════════════════════════════════════════════════════════
// MAIN PAGE COMPONENT
// ═════════════════════════════════════════════════════════════════════════════

export default function TreasurePage() {
  const { campaignId } = useParams();
  const { user, language } = useAuth();
  const { currentCampaign } = useCampaign();

  // ─── State ───────────────────────────────────────────────────────────
  const [activeTab, setActiveTab] = useState('generator');

  // Generator state
  const [generating, setGenerating] = useState(false);
  const [lastGenerated, setLastGenerated] = useState(null);
  const [generateParams, setGenerateParams] = useState({
    method: 'level', level: 5, cr: 5, location_type: 'dungeon', rarity: 'uncommon', budget_gp: 500
  });

  // Vault state
  const [treasures, setTreasures] = useState([]);
  const [treasuresLoading, setTreasuresLoading] = useState(true);
  const [selectedTreasure, setSelectedTreasure] = useState(null);
  const [editTreasureOpen, setEditTreasureOpen] = useState(false);

  // Library state
  const [equipment, setEquipment] = useState([]);
  const [equipmentLoading, setEquipmentLoading] = useState(true);
  const [equipmentDialogOpen, setEquipmentDialogOpen] = useState(false);
  const [editingEquipment, setEditingEquipment] = useState(null);
  const [detailEquipment, setDetailEquipment] = useState(null);
  const [detailOpen, setDetailOpen] = useState(false);
  const [deleteEquipment, setDeleteEquipment] = useState(null);
  const [equipSearch, setEquipSearch] = useState('');
  const [equipTypeFilter, setEquipTypeFilter] = useState('all');
  const [equipRarityFilter, setEquipRarityFilter] = useState('all');
  const [equipFavOnly, setEquipFavOnly] = useState(false);
  const [equipSort, setEquipSort] = useState('created_at');
  const [viewMode, setViewMode] = useState('grid');

  const userRole = currentCampaign?.members?.find(m => m.user_id === user?.id)?.role;
  const isMJ = isGameMaster(userRole);
  const generationPreview = useMemo(() => computeGenerationPreview(generateParams), [generateParams]);

  // ─── Data fetching ───────────────────────────────────────────────────

  const fetchTreasures = useCallback(async () => {
    setTreasuresLoading(true);
    try {
      const res = await axios.get(`${API_URL}/campaigns/${campaignId}/treasures`);
      setTreasures(res.data);
    } catch { /* ignore */ } finally { setTreasuresLoading(false); }
  }, [campaignId]);

  const fetchEquipment = useCallback(async () => {
    setEquipmentLoading(true);
    try {
      const params = {};
      if (equipTypeFilter && equipTypeFilter !== 'all') params.type = equipTypeFilter;
      if (equipRarityFilter && equipRarityFilter !== 'all') params.rarity = equipRarityFilter;
      if (equipSearch) params.search = equipSearch;
      if (equipFavOnly) params.favorite_only = true;
      params.sort = equipSort;
      const res = await axios.get(`${API_URL}/campaigns/${campaignId}/equipment`, { params });
      setEquipment(res.data);
    } catch { /* ignore */ } finally { setEquipmentLoading(false); }
  }, [campaignId, equipTypeFilter, equipRarityFilter, equipSearch, equipFavOnly, equipSort]);

  useEffect(() => { fetchTreasures(); }, [fetchTreasures]);
  useEffect(() => { fetchEquipment(); }, [fetchEquipment]);

  // ─── Stats ───────────────────────────────────────────────────────────

  const stats = useMemo(() => {
    const totalGP = treasures.reduce((sum, tr) => sum + calculateTotalGP(tr), 0);
    const totalItems = equipment.length;
    const totalFavs = equipment.filter(e => e.is_favorite).length;
    return { totalGP, totalItems, totalFavs };
  }, [treasures, equipment]);

  // ─── Generator handlers ──────────────────────────────────────────────

  const handleGenerate = async () => {
    setGenerating(true);
    try {
      const res = await axios.post(`${API_URL}/campaigns/${campaignId}/treasures/generate`, generateParams);
      setLastGenerated(res.data);
      setTreasures(prev => [res.data, ...prev]);
      toast.success(language === 'fr' ? 'Trésor généré !' : 'Treasure generated!');
    } catch {
      toast.error(t('error_generic', language));
    } finally { setGenerating(false); }
  };

  const handleSaveTreasure = async () => {
    if (!selectedTreasure) return;
    try {
      const res = await axios.patch(`${API_URL}/treasures/${selectedTreasure.id}`, {
        name: selectedTreasure.name, coins: selectedTreasure.coins,
        gems: selectedTreasure.gems, art_objects: selectedTreasure.art_objects,
        magic_items: selectedTreasure.magic_items, story_items: selectedTreasure.story_items
      });
      setTreasures(prev => prev.map(tr => tr.id === selectedTreasure.id ? res.data : tr));
      toast.success(t('success_saved', language));
    } catch { toast.error(t('error_generic', language)); }
  };

  const handleDeleteTreasure = async (treasureId) => {
    try {
      await axios.delete(`${API_URL}/treasures/${treasureId}`);
      setTreasures(prev => prev.filter(tr => tr.id !== treasureId));
      if (selectedTreasure?.id === treasureId) { setSelectedTreasure(null); setEditTreasureOpen(false); }
      toast.success(t('success_deleted', language));
    } catch { toast.error(t('error_generic', language)); }
  };

  const handleDistribute = async (treasureId) => {
    try {
      await axios.post(`${API_URL}/treasures/${treasureId}/distribute`, []);
      setTreasures(prev => prev.map(tr => tr.id === treasureId ? { ...tr, distributed: true } : tr));
      toast.success(language === 'fr' ? 'Trésor distribué !' : 'Treasure distributed!');
    } catch { toast.error(t('error_generic', language)); }
  };

  const handleConvertItem = async (treasureId, itemIndex) => {
    try {
      await axios.post(`${API_URL}/treasures/${treasureId}/convert-item?item_index=${itemIndex}`);
      toast.success(t('treasure_converted', language));
      fetchEquipment();
    } catch { toast.error(t('error_generic', language)); }
  };

  // ─── Equipment handlers ──────────────────────────────────────────────

  const handleEquipmentSaved = (data, action) => {
    if (action === 'create') setEquipment(prev => [data, ...prev]);
    else setEquipment(prev => prev.map(e => e.id === data.id ? data : e));
  };

  const handleToggleFavorite = async (id) => {
    try {
      const res = await axios.post(`${API_URL}/equipment/${id}/toggle-favorite`);
      setEquipment(prev => prev.map(e => e.id === id ? { ...e, is_favorite: res.data.is_favorite } : e));
    } catch { toast.error(t('error_generic', language)); }
  };

  const handleDuplicateEquipment = async (id) => {
    try {
      const res = await axios.post(`${API_URL}/equipment/${id}/duplicate`);
      setEquipment(prev => [res.data, ...prev]);
      toast.success(t('equipment_duplicated', language));
    } catch { toast.error(t('error_generic', language)); }
  };

  const handleDeleteEquipment = async () => {
    if (!deleteEquipment) return;
    try {
      await axios.delete(`${API_URL}/equipment/${deleteEquipment.id}`);
      setEquipment(prev => prev.filter(e => e.id !== deleteEquipment.id));
      toast.success(t('equipment_deleted', language));
    } catch { toast.error(t('error_generic', language)); }
    setDeleteEquipment(null);
  };

  // ─── Treasure edit helpers ───────────────────────────────────────────

  const addGem = () => setSelectedTreasure(prev => ({ ...prev, gems: [...(prev.gems || []), { name: '', value: 50, quantity: 1 }] }));
  const removeGem = (i) => setSelectedTreasure(prev => ({ ...prev, gems: prev.gems.filter((_, idx) => idx !== i) }));
  const addMagicItem = () => setSelectedTreasure(prev => ({ ...prev, magic_items: [...(prev.magic_items || []), { name: '', rarity: 'common', attunement: false, description: '', notes: '' }] }));
  const removeMagicItem = (i) => setSelectedTreasure(prev => ({ ...prev, magic_items: prev.magic_items.filter((_, idx) => idx !== i) }));

  // ─── Access check ────────────────────────────────────────────────────

  if (!isMJ) {
    return (
      <MainLayout>
        <Card className="glass-card">
          <CardContent className="py-16 text-center">
            <Gem className="h-16 w-16 mx-auto text-muted-foreground mb-6" />
            <h3 className="font-cinzel text-xl font-bold mb-2">
              {language === 'fr' ? 'Accès réservé au MJ' : 'GM only access'}
            </h3>
          </CardContent>
        </Card>
      </MainLayout>
    );
  }

  // ═══════════════════════════════════════════════════════════════════════
  // RENDER
  // ═══════════════════════════════════════════════════════════════════════

  return (
    <MainLayout>
      <div className="space-y-6 animate-fade-in" data-testid="treasure-page">

        {/* ─── Header with stats ─── */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <h1 className="font-cinzel text-3xl font-bold">
              {language === 'fr' ? 'Trésors & Équipements' : 'Treasure & Equipment'}
            </h1>
            <p className="text-muted-foreground mt-1">
              {language === 'fr'
                ? 'Générez, créez et gérez le loot de votre campagne'
                : 'Generate, create and manage your campaign loot'}
            </p>
          </div>
          <div className="flex items-center gap-3">
            <div className="text-center px-3 py-1 rounded-lg bg-amber-500/10 border border-amber-500/20">
              <div className="text-lg font-bold text-amber-500">{stats.totalGP.toLocaleString()}</div>
              <div className="text-[10px] text-muted-foreground uppercase">{t('treasure_stats_total', language)}</div>
            </div>
            <div className="text-center px-3 py-1 rounded-lg bg-blue-500/10 border border-blue-500/20">
              <div className="text-lg font-bold text-blue-500">{stats.totalItems}</div>
              <div className="text-[10px] text-muted-foreground uppercase">{t('treasure_stats_items', language)}</div>
            </div>
            <div className="text-center px-3 py-1 rounded-lg bg-purple-500/10 border border-purple-500/20">
              <div className="text-lg font-bold text-purple-500">{stats.totalFavs}</div>
              <div className="text-[10px] text-muted-foreground uppercase">{t('treasure_stats_favorites', language)}</div>
            </div>
          </div>
        </div>

        {/* ─── Tab Navigation ─── */}
        <Tabs value={activeTab} onValueChange={setActiveTab}>
          <TabsList className="grid grid-cols-1 sm:grid-cols-3 w-full max-w-md">
            <TabsTrigger value="generator">
              <Wand2 className="h-4 w-4 mr-1.5" />
              {t('treasure_tab_generator', language)}
            </TabsTrigger>
            <TabsTrigger value="library">
              <Crown className="h-4 w-4 mr-1.5" />
              {t('treasure_tab_library', language)}
            </TabsTrigger>
            <TabsTrigger value="vault">
              <Package className="h-4 w-4 mr-1.5" />
              {t('treasure_tab_vault', language)}
            </TabsTrigger>
          </TabsList>

          {/* ═══════════════════════════════════════════════════════════════ */}
          {/* TAB 1: GENERATOR                                              */}
          {/* ═══════════════════════════════════════════════════════════════ */}
          <TabsContent value="generator" className="mt-6">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">

              {/* Left: Configuration */}
              <Card className="glass-card">
                <CardHeader>
                  <CardTitle className="font-cinzel text-lg">
                    {t('treasure_method', language)}
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-6">
                  {/* Method select */}
                  <Select value={generateParams.method} onValueChange={v => setGenerateParams(prev => ({ ...prev, method: v }))}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="level">{t('treasure_method_level', language)}</SelectItem>
                      <SelectItem value="cr">{t('treasure_method_cr', language)}</SelectItem>
                      <SelectItem value="budget">{t('treasure_method_budget', language)}</SelectItem>
                      <SelectItem value="location">{t('treasure_method_location', language)}</SelectItem>
                      <SelectItem value="rarity">{t('treasure_method_rarity', language)}</SelectItem>
                    </SelectContent>
                  </Select>

                  {/* Location type */}
                  <div className="space-y-2">
                    <Label>{t('treasure_location_type', language)}</Label>
                    <Select value={generateParams.location_type} onValueChange={v => setGenerateParams(prev => ({ ...prev, location_type: v }))}>
                      <SelectTrigger><SelectValue /></SelectTrigger>
                      <SelectContent>
                        {Object.entries(LOCATION_LABELS[language] || LOCATION_LABELS.fr).map(([val, lbl]) => (
                          <SelectItem key={val} value={val}>{lbl}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>

                  {/* Rarity */}
                  <div className="space-y-2">
                    <Label>{t('treasure_target_rarity', language)}</Label>
                    <Select value={generateParams.rarity} onValueChange={v => setGenerateParams(prev => ({ ...prev, rarity: v }))}>
                      <SelectTrigger><SelectValue /></SelectTrigger>
                      <SelectContent>
                        {['common', 'uncommon', 'rare', 'very_rare', 'legendary'].map(r => (
                          <SelectItem key={r} value={r}>
                            <span className="flex items-center gap-2">
                              <div className={cn("h-2 w-2 rounded-full", RARITY_COLORS[r])} />
                              {t(`rarity_${r}`, language)}
                            </span>
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>

                  {/* Level slider */}
                  {['level', 'location', 'rarity'].includes(generateParams.method) && (
                    <div className="space-y-3">
                      <div className="flex items-center justify-between">
                        <Label>{t('treasure_party_level', language)}</Label>
                        <span className="text-2xl font-bold text-primary">{generateParams.level}</span>
                      </div>
                      <Slider value={[generateParams.level]} onValueChange={([v]) => setGenerateParams(prev => ({ ...prev, level: v }))} min={1} max={20} step={1} />
                    </div>
                  )}

                  {/* CR slider */}
                  {generateParams.method === 'cr' && (
                    <div className="space-y-3">
                      <div className="flex items-center justify-between">
                        <Label>{t('treasure_challenge_rating', language)}</Label>
                        <span className="text-2xl font-bold text-primary">{generateParams.cr}</span>
                      </div>
                      <Slider value={[generateParams.cr]} onValueChange={([v]) => setGenerateParams(prev => ({ ...prev, cr: v }))} min={1} max={30} step={1} />
                    </div>
                  )}

                  {/* Budget input */}
                  {generateParams.method === 'budget' && (
                    <div className="space-y-2">
                      <Label>{t('treasure_budget_gp', language)}</Label>
                      <Input type="number" value={generateParams.budget_gp} onChange={e => setGenerateParams(prev => ({ ...prev, budget_gp: parseInt(e.target.value) || 0 }))} min={0} />
                    </div>
                  )}

                  {/* Preview */}
                  <div className="rounded-lg border border-border/60 bg-muted/20 p-4 space-y-3" data-testid="treasure-generation-preview">
                    <div className="flex items-center justify-between">
                      <Label className="text-sm font-medium">{t('treasure_estimate', language)}</Label>
                      <Badge variant="outline" className="text-xs">
                        {language === 'fr' ? `Niveau ${generationPreview.level}` : `Level ${generationPreview.level}`}
                      </Badge>
                    </div>
                    <div className="grid grid-cols-2 gap-2 text-xs">
                      <div className="text-muted-foreground">{language === 'fr' ? 'Valeur estimée' : 'Estimated value'}</div>
                      <div className="text-right font-medium">~{generationPreview.targetGp} GP</div>
                      <div className="text-muted-foreground">{language === 'fr' ? 'Plage' : 'Range'}</div>
                      <div className="text-right font-medium">{generationPreview.minGp} - {generationPreview.maxGp} GP</div>
                      <div className="text-muted-foreground">{t('treasure_section_magic', language)}</div>
                      <div className="text-right font-medium">{generationPreview.magicChance}%</div>
                    </div>
                    <div className="pt-1 border-t border-border/60 grid grid-cols-1 sm:grid-cols-3 gap-2 text-xs">
                      <div className="rounded bg-background/50 p-2 text-center">
                        <div className="text-muted-foreground">{t('treasure_section_coins', language)}</div>
                        <div className="font-semibold">{generationPreview.coinsGp} GP</div>
                      </div>
                      <div className="rounded bg-background/50 p-2 text-center">
                        <div className="text-muted-foreground">{t('treasure_section_gems', language)}</div>
                        <div className="font-semibold">{generationPreview.gemsGp} GP</div>
                      </div>
                      <div className="rounded bg-background/50 p-2 text-center">
                        <div className="text-muted-foreground">{t('treasure_section_art', language)}</div>
                        <div className="font-semibold">{generationPreview.artGp} GP</div>
                      </div>
                    </div>
                  </div>

                  {/* Generate button */}
                  <Button onClick={handleGenerate} disabled={generating} className="fantasy-button w-full" data-testid="generate-submit">
                    {generating ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Sparkles className="mr-2 h-4 w-4" />}
                    {t('treasure_generate_btn', language)}
                  </Button>
                </CardContent>
              </Card>

              {/* Right: Result */}
              <Card className="glass-card">
                <CardHeader>
                  <CardTitle className="font-cinzel text-lg">
                    {language === 'fr' ? 'Résultat' : 'Result'}
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  {lastGenerated ? (
                    <div className="space-y-4">
                      {/* Coins section */}
                      {lastGenerated.coins && (
                        <div className="space-y-2">
                          <div className="flex items-center gap-2 text-sm font-medium">
                            <Coins className="h-4 w-4 text-amber-500" />
                            {t('treasure_section_coins', language)}
                          </div>
                          <div className="flex flex-wrap gap-2">
                            {Object.entries(lastGenerated.coins).filter(([, v]) => v > 0).map(([coin, val]) => (
                              <Badge key={coin} variant="outline">{val} {coin.toUpperCase()}</Badge>
                            ))}
                          </div>
                        </div>
                      )}

                      {/* Gems section */}
                      {lastGenerated.gems?.length > 0 && (
                        <div className="space-y-2">
                          <div className="flex items-center gap-2 text-sm font-medium">
                            <Gem className="h-4 w-4 text-cyan-500" />
                            {t('treasure_section_gems', language)}
                            <Badge variant="secondary" className="text-xs">{lastGenerated.gems.length}</Badge>
                          </div>
                          {lastGenerated.gems.map((gem, i) => (
                            <div key={i} className="text-sm pl-6 flex justify-between">
                              <span>{gem.name} {gem.quantity > 1 ? `(x${gem.quantity})` : ''}</span>
                              <span className="text-muted-foreground">{gem.value} gp</span>
                            </div>
                          ))}
                        </div>
                      )}

                      {/* Art objects section */}
                      {lastGenerated.art_objects?.length > 0 && (
                        <div className="space-y-2">
                          <div className="flex items-center gap-2 text-sm font-medium">
                            <Crown className="h-4 w-4 text-pink-500" />
                            {t('treasure_section_art', language)}
                            <Badge variant="secondary" className="text-xs">{lastGenerated.art_objects.length}</Badge>
                          </div>
                          {lastGenerated.art_objects.map((art, i) => (
                            <div key={i} className="text-sm pl-6 flex justify-between">
                              <span>{art.name}</span>
                              <span className="text-muted-foreground">{art.value} gp</span>
                            </div>
                          ))}
                        </div>
                      )}

                      {/* Magic items section */}
                      {lastGenerated.magic_items?.length > 0 && (
                        <div className="space-y-2">
                          <div className="flex items-center gap-2 text-sm font-medium">
                            <Sparkles className="h-4 w-4 text-purple-500" />
                            {t('treasure_section_magic', language)}
                            <Badge variant="secondary" className="text-xs">{lastGenerated.magic_items.length}</Badge>
                          </div>
                          {lastGenerated.magic_items.map((mi, i) => (
                            <div key={i} className="flex items-center justify-between pl-6 gap-2">
                              <div className="flex items-center gap-2 text-sm min-w-0">
                                <div className={cn("h-2 w-2 rounded-full shrink-0", RARITY_COLORS[mi.rarity])} />
                                <span className="truncate">{mi.name}</span>
                              </div>
                              <Button variant="ghost" size="sm" className="text-xs shrink-0 h-7"
                                onClick={() => handleConvertItem(lastGenerated.id, i)}>
                                <Crown className="h-3 w-3 mr-1" />
                                {t('treasure_convert_to_card', language)}
                              </Button>
                            </div>
                          ))}
                        </div>
                      )}

                      {/* Story items section */}
                      {lastGenerated.story_items?.length > 0 && (
                        <div className="space-y-2">
                          <div className="flex items-center gap-2 text-sm font-medium">
                            <ScrollText className="h-4 w-4 text-green-500" />
                            {t('treasure_section_story', language)}
                            <Badge variant="secondary" className="text-xs">{lastGenerated.story_items.length}</Badge>
                          </div>
                          {lastGenerated.story_items.map((si, i) => (
                            <div key={i} className="text-sm pl-6">
                              <span className="font-medium">{si.name}</span>
                              {si.description && <p className="text-muted-foreground text-xs mt-0.5">{si.description}</p>}
                            </div>
                          ))}
                        </div>
                      )}

                      {/* Total */}
                      <div className="pt-3 border-t border-border flex items-center justify-between">
                        <span className="text-sm font-medium">{t('treasure_stats_total', language)}</span>
                        <span className="text-lg font-bold text-amber-500">{calculateTotalGP(lastGenerated)} GP</span>
                      </div>

                      {/* Actions */}
                      <div className="flex gap-2">
                        <Button variant="outline" className="flex-1" onClick={handleGenerate} disabled={generating}>
                          <RefreshCw className={cn("mr-2 h-4 w-4", generating && "animate-spin")} />
                          {t('treasure_reroll', language)}
                        </Button>
                        <Button variant="outline" className="flex-1" onClick={() => {
                          setSelectedTreasure(lastGenerated);
                          setEditTreasureOpen(true);
                        }}>
                          <Edit className="mr-2 h-4 w-4" />
                          {t('edit', language)}
                        </Button>
                      </div>
                    </div>
                  ) : (
                    <div className="py-12 text-center text-muted-foreground">
                      <Wand2 className="h-12 w-12 mx-auto mb-4 opacity-30" />
                      <p>{language === 'fr' ? 'Configurez et lancez une génération' : 'Configure and start a generation'}</p>
                    </div>
                  )}
                </CardContent>
              </Card>
            </div>
          </TabsContent>

          {/* ═══════════════════════════════════════════════════════════════ */}
          {/* TAB 2: LIBRARY                                                */}
          {/* ═══════════════════════════════════════════════════════════════ */}
          <TabsContent value="library" className="mt-6">
            {/* Toolbar */}
            <div className="flex flex-col md:flex-row md:items-center gap-3 mb-6">
              <Button className="fantasy-button" onClick={() => { setEditingEquipment(null); setEquipmentDialogOpen(true); }}>
                <Plus className="mr-2 h-4 w-4" />
                {t('equipment_create', language)}
              </Button>

              <div className="flex-1 relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                <Input
                  className="pl-9"
                  placeholder={t('search', language)}
                  value={equipSearch}
                  onChange={e => setEquipSearch(e.target.value)}
                />
              </div>

              <div className="flex items-center gap-2 flex-wrap">
                <Select value={equipTypeFilter} onValueChange={setEquipTypeFilter}>
                  <SelectTrigger className="w-[140px]"><SelectValue placeholder={t('filter_all_types', language)} /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">{t('filter_all_types', language)}</SelectItem>
                    {EQUIPMENT_TYPES.map(tp => (
                      <SelectItem key={tp} value={tp}>{t(`equipment_type_${tp}`, language)}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>

                <Select value={equipRarityFilter} onValueChange={setEquipRarityFilter}>
                  <SelectTrigger className="w-[140px]"><SelectValue placeholder={t('filter_all_rarities', language)} /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">{t('filter_all_rarities', language)}</SelectItem>
                    {EQUIPMENT_RARITIES.map(r => (
                      <SelectItem key={r} value={r}>
                        <span className="flex items-center gap-2">
                          <div className={cn("h-2 w-2 rounded-full", RARITY_COLORS[r])} />
                          {t(`rarity_${r}`, language)}
                        </span>
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>

                <Button variant={equipFavOnly ? "default" : "outline"} size="icon" onClick={() => setEquipFavOnly(!equipFavOnly)}>
                  <Star className={cn("h-4 w-4", equipFavOnly && "fill-current")} />
                </Button>

                <Button variant="outline" size="icon" onClick={() => setViewMode(viewMode === 'grid' ? 'list' : 'grid')}>
                  {viewMode === 'grid' ? <List className="h-4 w-4" /> : <Grid3X3 className="h-4 w-4" />}
                </Button>
              </div>
            </div>

            {/* Collection counter */}
            <div className="text-sm text-muted-foreground mb-4">
              {equipment.length} {t('equipment_collection_count', language)}
            </div>

            {/* Equipment grid/list */}
            {equipmentLoading ? (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
                {[1, 2, 3, 4].map(i => (
                  <Card key={i} className="glass-card">
                    <CardContent className="p-6">
                      <div className="animate-pulse space-y-4">
                        <div className="h-32 bg-muted rounded" />
                        <div className="h-4 bg-muted rounded w-3/4" />
                        <div className="h-3 bg-muted rounded w-1/2" />
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            ) : equipment.length === 0 ? (
              <Card className="glass-card">
                <CardContent className="py-16 text-center">
                  <Crown className="h-16 w-16 mx-auto text-muted-foreground mb-6" />
                  <h3 className="font-cinzel text-xl font-bold mb-2">{t('equipment_no_items', language)}</h3>
                  <p className="text-muted-foreground mb-8 max-w-md mx-auto">
                    {t('equipment_no_items_desc', language)}
                  </p>
                  <Button className="fantasy-button" onClick={() => { setEditingEquipment(null); setEquipmentDialogOpen(true); }}>
                    <Plus className="mr-2 h-4 w-4" />
                    {t('equipment_create', language)}
                  </Button>
                </CardContent>
              </Card>
            ) : viewMode === 'grid' ? (
              <div className="equipment-cards-grid">
                {equipment.map(item => (
                  <Equipment3DCard
                    key={item.id}
                    item={item}
                    language={language}
                    onEdit={(eq) => { setDetailEquipment(eq); setDetailOpen(true); }}
                    onToggleFavorite={handleToggleFavorite}
                    onDuplicate={handleDuplicateEquipment}
                    onDelete={(eq) => setDeleteEquipment(eq)}
                  />
                ))}
              </div>
            ) : (
              <div className="space-y-2">
                {equipment.map(item => {
                  const TypeIcon = TYPE_ICONS[item.type] || Sparkles;
                  return (
                    <div key={item.id}
                      className={cn("flex items-center gap-4 p-3 rounded-lg border cursor-pointer hover:bg-accent/50 transition-colors", RARITY_BORDER_COLORS[item.rarity])}
                      onClick={() => { setDetailEquipment(item); setDetailOpen(true); }}
                    >
                      <TypeIcon className={cn("h-5 w-5 shrink-0", RARITY_TEXT_COLORS[item.rarity])} />
                      <div className="flex-1 min-w-0">
                        <div className="font-medium text-sm truncate">{item.name}</div>
                        {item.effect && <p className="text-xs text-muted-foreground truncate">{item.effect}</p>}
                      </div>
                      <Badge className={cn("text-xs text-white shrink-0", RARITY_COLORS[item.rarity])}>
                        {t(`rarity_${item.rarity}`, language)}
                      </Badge>
                      {item.value_gp > 0 && <span className="text-xs text-amber-500 shrink-0">{item.value_gp} gp</span>}
                      {item.is_favorite && <Star className="h-3.5 w-3.5 fill-amber-500 text-amber-500 shrink-0" />}
                      <div className="flex gap-1 shrink-0">
                        <Button variant="ghost" size="icon" className="h-7 w-7"
                          onClick={e => { e.stopPropagation(); setEditingEquipment(item); setEquipmentDialogOpen(true); }}>
                          <Edit className="h-3.5 w-3.5" />
                        </Button>
                        <Button variant="ghost" size="icon" className="h-7 w-7 text-destructive"
                          onClick={e => { e.stopPropagation(); setDeleteEquipment(item); }}>
                          <Trash2 className="h-3.5 w-3.5" />
                        </Button>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </TabsContent>

          {/* ═══════════════════════════════════════════════════════════════ */}
          {/* TAB 3: VAULT                                                  */}
          {/* ═══════════════════════════════════════════════════════════════ */}
          <TabsContent value="vault" className="mt-6">
            {treasuresLoading ? (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {[1, 2, 3].map(i => (
                  <Card key={i} className="glass-card">
                    <CardContent className="p-6">
                      <div className="animate-pulse space-y-4">
                        <div className="h-6 bg-muted rounded w-3/4" />
                        <div className="h-4 bg-muted rounded w-1/2" />
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            ) : treasures.length === 0 ? (
              <Card className="glass-card">
                <CardContent className="py-16 text-center">
                  <Package className="h-16 w-16 mx-auto text-muted-foreground mb-6" />
                  <h3 className="font-cinzel text-xl font-bold mb-2">{t('vault_empty', language)}</h3>
                  <p className="text-muted-foreground mb-8 max-w-md mx-auto">
                    {t('vault_empty_desc', language)}
                  </p>
                  <Button className="fantasy-button" onClick={() => setActiveTab('generator')}>
                    <Wand2 className="mr-2 h-4 w-4" />
                    {t('treasure_tab_generator', language)}
                  </Button>
                </CardContent>
              </Card>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {treasures.map(treasure => (
                  <Card
                    key={treasure.id}
                    className={cn("glass-card card-interactive cursor-pointer", treasure.distributed && "opacity-60")}
                    onClick={() => { setSelectedTreasure(treasure); setEditTreasureOpen(true); }}
                    data-testid={`treasure-card-${treasure.id}`}
                  >
                    <CardHeader className="pb-2">
                      <div className="flex items-start justify-between">
                        <CardTitle className="text-lg">{treasure.name}</CardTitle>
                        <Badge variant="secondary" className={treasure.distributed ? "bg-green-500/10 text-green-500" : "bg-blue-500/10 text-blue-500"}>
                          {treasure.distributed ? (
                            <><Check className="h-3 w-3 mr-1" />{t('vault_distributed', language)}</>
                          ) : (
                            t('vault_in_reserve', language)
                          )}
                        </Badge>
                      </div>
                      <CardDescription>
                        {formatDate(treasure.created_at, language === 'fr' ? 'fr-FR' : 'en-US')}
                        {treasure.generated_method && (
                          <Badge variant="outline" className="ml-2 text-[10px]">{treasure.generated_method}</Badge>
                        )}
                      </CardDescription>
                    </CardHeader>
                    <CardContent>
                      <div className="space-y-2">
                        <div className="flex items-center gap-2 text-sm">
                          <Coins className="h-4 w-4 text-amber-500" />
                          <span className="font-medium">{calculateTotalGP(treasure)} GP</span>
                        </div>
                        <div className="flex flex-wrap gap-2">
                          {treasure.gems?.length > 0 && (
                            <Badge variant="outline">
                              <Gem className="h-3 w-3 mr-1" />{treasure.gems.length} {t('treasure_section_gems', language).toLowerCase()}
                            </Badge>
                          )}
                          {treasure.art_objects?.length > 0 && (
                            <Badge variant="outline">
                              <Crown className="h-3 w-3 mr-1" />{treasure.art_objects.length}
                            </Badge>
                          )}
                          {treasure.magic_items?.length > 0 && (
                            <Badge variant="outline" className="border-purple-500/50 text-purple-500">
                              <Sparkles className="h-3 w-3 mr-1" />{treasure.magic_items.length}
                            </Badge>
                          )}
                          {treasure.story_items?.length > 0 && (
                            <Badge variant="outline" className="border-green-500/50 text-green-500">
                              <ScrollText className="h-3 w-3 mr-1" />{treasure.story_items.length}
                            </Badge>
                          )}
                        </div>
                        {treasure.magic_items?.length > 0 && (
                          <div className="pt-2 border-t border-border mt-2">
                            {treasure.magic_items.slice(0, 2).map((item, i) => (
                              <div key={i} className="flex items-center gap-2 text-sm">
                                <div className={cn("h-2 w-2 rounded-full", RARITY_COLORS[item.rarity])} />
                                <span className="truncate">{item.name}</span>
                              </div>
                            ))}
                            {treasure.magic_items.length > 2 && (
                              <p className="text-xs text-muted-foreground">+{treasure.magic_items.length - 2} {language === 'fr' ? 'autres' : 'more'}</p>
                            )}
                          </div>
                        )}
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            )}
          </TabsContent>
        </Tabs>

        {/* ═══════════════════════════════════════════════════════════════ */}
        {/* DIALOGS                                                       */}
        {/* ═══════════════════════════════════════════════════════════════ */}

        {/* Equipment Create/Edit Dialog */}
        <EquipmentDialog
          open={equipmentDialogOpen}
          onOpenChange={setEquipmentDialogOpen}
          equipment={editingEquipment}
          language={language}
          campaignId={campaignId}
          onSaved={handleEquipmentSaved}
          campaignMembers={currentCampaign?.members || []}
        />

        {/* Equipment Detail Dialog */}
        <EquipmentDetailDialog
          open={detailOpen}
          onOpenChange={setDetailOpen}
          item={detailEquipment}
          language={language}
          onEdit={(eq) => { setEditingEquipment(eq); setEquipmentDialogOpen(true); }}
          onDuplicate={handleDuplicateEquipment}
          onToggleFavorite={handleToggleFavorite}
          onDelete={(eq) => setDeleteEquipment(eq)}
        />

        {/* Delete Equipment Confirm */}
        <AlertDialog open={!!deleteEquipment} onOpenChange={(open) => { if (!open) setDeleteEquipment(null); }}>
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle>{t('equipment_delete', language)}</AlertDialogTitle>
              <AlertDialogDescription>{t('equipment_delete_confirm', language)}</AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel>{t('cancel', language)}</AlertDialogCancel>
              <AlertDialogAction onClick={handleDeleteEquipment} className="bg-destructive text-destructive-foreground hover:bg-destructive/90">
                {t('delete', language)}
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>

        {/* Edit Treasure Dialog */}
        <Dialog open={editTreasureOpen} onOpenChange={setEditTreasureOpen}>
          <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
            {selectedTreasure && (
              <>
                <DialogHeader>
                  <DialogTitle className="font-cinzel">
                    <Input
                      value={selectedTreasure.name}
                      onChange={e => setSelectedTreasure(prev => ({ ...prev, name: e.target.value }))}
                      className="font-cinzel text-xl font-bold border-none p-0 h-auto"
                    />
                  </DialogTitle>
                </DialogHeader>

                <Tabs defaultValue="coins" className="mt-4">
                  <TabsList className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-5 w-full">
                    <TabsTrigger value="coins"><Coins className="h-4 w-4 mr-1" />{t('treasure_section_coins', language)}</TabsTrigger>
                    <TabsTrigger value="gems"><Gem className="h-4 w-4 mr-1" />{t('treasure_section_gems', language)}</TabsTrigger>
                    <TabsTrigger value="art"><Crown className="h-4 w-4 mr-1" />{t('treasure_section_art', language)}</TabsTrigger>
                    <TabsTrigger value="magic"><Sparkles className="h-4 w-4 mr-1" />{t('treasure_section_magic', language)}</TabsTrigger>
                    <TabsTrigger value="story"><ScrollText className="h-4 w-4 mr-1" />{t('treasure_section_story', language)}</TabsTrigger>
                  </TabsList>

                  {/* Coins Tab */}
                  <TabsContent value="coins" className="space-y-4 mt-4">
                    <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-5 gap-4">
                      {['cp', 'sp', 'ep', 'gp', 'pp'].map(coin => (
                        <div key={coin} className="space-y-1">
                          <Label className="text-xs uppercase">{coin}</Label>
                          <Input type="number" min={0} value={selectedTreasure.coins?.[coin] || 0}
                            onChange={e => setSelectedTreasure(prev => ({ ...prev, coins: { ...prev.coins, [coin]: parseInt(e.target.value) || 0 } }))} />
                        </div>
                      ))}
                    </div>
                    <p className="text-sm text-muted-foreground text-center">
                      {language === 'fr' ? 'Valeur totale' : 'Total value'}: <strong>{calculateTotalGP(selectedTreasure)} GP</strong>
                    </p>
                  </TabsContent>

                  {/* Gems Tab */}
                  <TabsContent value="gems" className="space-y-4 mt-4">
                    {selectedTreasure.gems?.map((gem, index) => (
                      <div key={index} className="flex items-center gap-2 p-2 rounded-lg bg-secondary/30">
                        <Input placeholder={language === 'fr' ? 'Nom' : 'Name'} value={gem.name} className="flex-1"
                          onChange={e => { const g = [...selectedTreasure.gems]; g[index] = { ...g[index], name: e.target.value }; setSelectedTreasure(prev => ({ ...prev, gems: g })); }} />
                        <Input type="number" placeholder="GP" value={gem.value} className="w-24"
                          onChange={e => { const g = [...selectedTreasure.gems]; g[index] = { ...g[index], value: parseInt(e.target.value) || 0 }; setSelectedTreasure(prev => ({ ...prev, gems: g })); }} />
                        <Input type="number" placeholder="Qty" value={gem.quantity} className="w-16"
                          onChange={e => { const g = [...selectedTreasure.gems]; g[index] = { ...g[index], quantity: parseInt(e.target.value) || 1 }; setSelectedTreasure(prev => ({ ...prev, gems: g })); }} />
                        <Button variant="ghost" size="icon" onClick={() => removeGem(index)}><X className="h-4 w-4" /></Button>
                      </div>
                    ))}
                    <Button variant="outline" onClick={addGem} className="w-full">
                      <Plus className="h-4 w-4 mr-2" />{language === 'fr' ? 'Ajouter une gemme' : 'Add gem'}
                    </Button>
                  </TabsContent>

                  {/* Art Objects Tab */}
                  <TabsContent value="art" className="space-y-4 mt-4">
                    {selectedTreasure.art_objects?.map((art, index) => (
                      <div key={index} className="flex items-center gap-2 p-2 rounded-lg bg-secondary/30">
                        <Input placeholder={language === 'fr' ? 'Nom' : 'Name'} value={art.name} className="flex-1"
                          onChange={e => { const a = [...selectedTreasure.art_objects]; a[index] = { ...a[index], name: e.target.value }; setSelectedTreasure(prev => ({ ...prev, art_objects: a })); }} />
                        <Input type="number" placeholder="GP" value={art.value} className="w-24"
                          onChange={e => { const a = [...selectedTreasure.art_objects]; a[index] = { ...a[index], value: parseInt(e.target.value) || 0 }; setSelectedTreasure(prev => ({ ...prev, art_objects: a })); }} />
                        <Button variant="ghost" size="icon" onClick={() => {
                          setSelectedTreasure(prev => ({ ...prev, art_objects: prev.art_objects.filter((_, i) => i !== index) }));
                        }}><X className="h-4 w-4" /></Button>
                      </div>
                    ))}
                    <Button variant="outline" onClick={() => {
                      setSelectedTreasure(prev => ({ ...prev, art_objects: [...(prev.art_objects || []), { name: '', value: 25, description: '' }] }));
                    }} className="w-full">
                      <Plus className="h-4 w-4 mr-2" />{language === 'fr' ? "Ajouter un objet d'art" : 'Add art object'}
                    </Button>
                  </TabsContent>

                  {/* Magic Items Tab */}
                  <TabsContent value="magic" className="space-y-4 mt-4">
                    {selectedTreasure.magic_items?.map((item, index) => (
                      <div key={index} className="p-3 rounded-lg bg-secondary/30 space-y-2">
                        <div className="flex items-center gap-2">
                          <Input placeholder={language === 'fr' ? "Nom de l'objet" : 'Item name'} value={item.name} className="flex-1"
                            onChange={e => { const m = [...selectedTreasure.magic_items]; m[index] = { ...m[index], name: e.target.value }; setSelectedTreasure(prev => ({ ...prev, magic_items: m })); }} />
                          <Select value={item.rarity} onValueChange={v => {
                            const m = [...selectedTreasure.magic_items]; m[index] = { ...m[index], rarity: v }; setSelectedTreasure(prev => ({ ...prev, magic_items: m }));
                          }}>
                            <SelectTrigger className="w-32"><SelectValue /></SelectTrigger>
                            <SelectContent>
                              {Object.keys(RARITY_COLORS).map(r => (
                                <SelectItem key={r} value={r}>
                                  <span className="flex items-center gap-2"><div className={cn("h-2 w-2 rounded-full", RARITY_COLORS[r])} />{t(`rarity_${r}`, language)}</span>
                                </SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                          <Button variant="ghost" size="icon" onClick={() => removeMagicItem(index)}><X className="h-4 w-4" /></Button>
                        </div>
                        <div className="flex items-center gap-4">
                          <div className="flex items-center gap-2">
                            <Switch checked={item.attunement} onCheckedChange={v => {
                              const m = [...selectedTreasure.magic_items]; m[index] = { ...m[index], attunement: v }; setSelectedTreasure(prev => ({ ...prev, magic_items: m }));
                            }} />
                            <Label className="text-sm">{language === 'fr' ? 'Harmonisation' : 'Attunement'}</Label>
                          </div>
                          <Button variant="ghost" size="sm" className="text-xs ml-auto"
                            onClick={() => handleConvertItem(selectedTreasure.id, index)}>
                            <Crown className="h-3 w-3 mr-1" />{t('treasure_convert_to_card', language)}
                          </Button>
                        </div>
                        <Textarea placeholder={language === 'fr' ? 'Notes...' : 'Notes...'} value={item.notes || ''} rows={2}
                          onChange={e => { const m = [...selectedTreasure.magic_items]; m[index] = { ...m[index], notes: e.target.value }; setSelectedTreasure(prev => ({ ...prev, magic_items: m })); }} />
                      </div>
                    ))}
                    <Button variant="outline" onClick={addMagicItem} className="w-full">
                      <Plus className="h-4 w-4 mr-2" />{language === 'fr' ? 'Ajouter un objet magique' : 'Add magic item'}
                    </Button>
                  </TabsContent>

                  {/* Story Items Tab */}
                  <TabsContent value="story" className="space-y-4 mt-4">
                    {selectedTreasure.story_items?.map((item, index) => (
                      <div key={index} className="p-3 rounded-lg bg-secondary/30 space-y-2">
                        <div className="flex items-center gap-2">
                          <Input placeholder={language === 'fr' ? 'Nom' : 'Name'} value={item.name} className="flex-1"
                            onChange={e => { const s = [...selectedTreasure.story_items]; s[index] = { ...s[index], name: e.target.value }; setSelectedTreasure(prev => ({ ...prev, story_items: s })); }} />
                          <Button variant="ghost" size="icon" onClick={() => {
                            setSelectedTreasure(prev => ({ ...prev, story_items: prev.story_items.filter((_, i) => i !== index) }));
                          }}><X className="h-4 w-4" /></Button>
                        </div>
                        <Textarea placeholder={language === 'fr' ? 'Description...' : 'Description...'} value={item.description || ''} rows={2}
                          onChange={e => { const s = [...selectedTreasure.story_items]; s[index] = { ...s[index], description: e.target.value }; setSelectedTreasure(prev => ({ ...prev, story_items: s })); }} />
                      </div>
                    ))}
                    <Button variant="outline" onClick={() => {
                      setSelectedTreasure(prev => ({ ...prev, story_items: [...(prev.story_items || []), { name: '', description: '' }] }));
                    }} className="w-full">
                      <Plus className="h-4 w-4 mr-2" />{language === 'fr' ? 'Ajouter un objet narratif' : 'Add story item'}
                    </Button>
                  </TabsContent>
                </Tabs>

                <DialogFooter className="mt-6 flex justify-between">
                  <div className="flex gap-2">
                    <Button variant="destructive" size="sm" onClick={() => handleDeleteTreasure(selectedTreasure.id)}>
                      <Trash2 className="h-4 w-4 mr-1" />{t('delete', language)}
                    </Button>
                    {!selectedTreasure.distributed && (
                      <Button variant="outline" size="sm" onClick={() => handleDistribute(selectedTreasure.id)}>
                        <Package className="h-4 w-4 mr-1" />{t('vault_distribute', language)}
                      </Button>
                    )}
                  </div>
                  <div className="flex gap-2">
                    <Button variant="outline" onClick={() => setEditTreasureOpen(false)}>{t('close', language)}</Button>
                    <Button onClick={handleSaveTreasure}>{t('save', language)}</Button>
                  </div>
                </DialogFooter>
              </>
            )}
          </DialogContent>
        </Dialog>
      </div>
    </MainLayout>
  );
}
